import type { types } from 'mediasoup-client';
import { serializeError } from 'serialize-error';
import { DEFAULT_AUDIO_ENCODINGS } from '../../../constants/encodings';
import { CLIENT_EVENTS, INTERNAL_CLIENT_EVENTS } from '../../../constants/events';
import type EnhancedEventEmitter from '../../../EnhancedEventEmitter';
import OperationError from '../../../errors/OperationError';
import type { AudioCodec, AudioEncoderConfig } from '../../../types/common';
import type { AudioTrackProcessorFactory } from '../../../types/container';
import type { ClientObserverEvents } from '../../../types/engine';
import type { AudioTrack, AudioTrackParams, BaseTrack, BaseTrackDependenciesParams } from '../../../types/media';
import type { AudioTrackProcessor } from '../streamEffects/audio/AudioTrackProcessor';
import DefaultBaseTrack from './DefaultBaseTrack';

export type AudioTrackDependenciesParams = BaseTrackDependenciesParams & {
  processorFactory: AudioTrackProcessorFactory;
  clientEventEmitter: EnhancedEventEmitter<ClientObserverEvents>;
};
export type AudioTracksConstructorParams = AudioTrackParams & AudioTrackDependenciesParams;

class DefaultAudioTrack extends DefaultBaseTrack implements AudioTrack {
  #denoiserEnabled = false; // to track actions performed by SPA

  #trackProcessor?: AudioTrackProcessor;

  #originalTrack?: MediaStreamTrack;

  readonly #clientEventEmitter: EnhancedEventEmitter<ClientObserverEvents>;

  constructor(params: AudioTracksConstructorParams) {
    super(params);

    this.#clientEventEmitter = params.clientEventEmitter;
  }

  get isDenoiserRequireActivation(): boolean {
    return !this.#trackProcessor?.isProcessing && this.#denoiserEnabled;
  }

  setTrackProcessor(trackProcessor?: AudioTrackProcessor): void {
    if (!trackProcessor) {
      throw new OperationError('track_processor_missing', new Error('trackProcessor must be passed as parameter'));
    }

    this.logger.debug('Setting trackProcessor to AudioTrack', {
      case: 'Denoiser',
      type: trackProcessor.type,
    });

    this.#trackProcessor = trackProcessor;
  }

  unsetTrackProcessor(): void {
    this.#trackProcessor = undefined;
  }

  get isEffectsProcessing(): boolean {
    return Boolean(this.#trackProcessor?.isProcessing);
  }

  getEncoderConfig(): AudioEncoderConfig {
    return this.encoderConfig;
  }

  getEncodings(): types.RtpEncodingParameters[] {
    return this.getEncoderConfig().encodings ?? DEFAULT_AUDIO_ENCODINGS;
  }

  getCodecOptions(): types.ProducerCodecOptions {
    return {
      opusFec: this.getEncoderConfig().enableFec ?? true,
      opusDtx: this.getEncoderConfig().enableDtx ?? true,
    };
  }

  getPreferredCodec(): AudioCodec {
    const { preferredCodec } = this.getEncoderConfig();

    if (preferredCodec) {
      return preferredCodec;
    }

    return 'opus';
  }

  async enableNoiseSuppression(): Promise<void> {
    this.#denoiserEnabled = true; // to mark that denoiser should automatically be activated when available

    if (!this.#trackProcessor) {
      this.logger.warn('enableNoiseSuppression()', { description: 'Track suppressor is not defined' });
      this.#clientEventEmitter.emit(CLIENT_EVENTS.denoiserFailed, {
        cause: 'Track suppressor is not defined',
      });

      return;
    }

    try {
      const processedTrack = await this.#trackProcessor?.processTrack(this.mediaStreamTrack);
      this.#originalTrack = this.mediaStreamTrack;
      await this.replaceTrack(processedTrack);

      this.logger.debug('Denoiser activated', {
        case: 'Denoiser',
        type: this.#trackProcessor?.type || 'none',
      });

      this.#clientEventEmitter.emit(CLIENT_EVENTS.denoiserEnabled, { type: this.#trackProcessor?.type });
    } catch (error) {
      this.logger.warn('enableNoiseSuppression()', {
        description: 'Can not enable noise suppression',
        track: this.logCtx,
        error: serializeError(error),
      });

      this.#clientEventEmitter.emit(CLIENT_EVENTS.denoiserFailed, {
        cause: 'Failed to enable noise suppression',
        error,
      });
      throw new OperationError('noise_suppression_enable_failed', error);
    }
  }

  async disableNoiseSuppression(): Promise<void> {
    this.#denoiserEnabled = false;

    try {
      if (!this.#trackProcessor?.isProcessing) {
        this.logger.debug('stopProcessing()', { description: 'Track is not processed' });

        return;
      }

      if (!this.#originalTrack) {
        this.logger.debug('disableNoiseSuppression()', { description: 'Original track missing' });

        return;
      }

      await this.replaceTrack(this.#originalTrack);
      this.#trackProcessor?.stopProcessing();

      this.logger.debug('Denoiser deactivated', {
        case: 'Denoiser',
        type: this.#trackProcessor?.type || 'none',
      });

      this.#clientEventEmitter.emit(CLIENT_EVENTS.denoiserDisabled, { type: this.#trackProcessor?.type });
    } catch (error) {
      this.logger.warn('disableNoiseSuppression()', {
        description: 'Can not disable noise suppression',
        track: this.logCtx,
        error: serializeError(error),
      });

      this.#clientEventEmitter.emit(CLIENT_EVENTS.denoiserFailed, {
        cause: 'Failed to disable noise suppression',
        error,
      });
      throw new OperationError('noise_suppression_disable_failed', error);
    }
  }

  async publish(): Promise<void> {
    await this.produce({
      encodings: this.getEncodings(),
      preferredCodec: this.getPreferredCodec(),
      codecOptions: this.getCodecOptions(),
    });

    this.internalEventEmitter.emit(INTERNAL_CLIENT_EVENTS.trackProduced, this as unknown as BaseTrack);
  }

  public async pause(): Promise<void> {
    await this.mute();
  }
}

export default DefaultAudioTrack;
